import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { X, ChevronRight, ChevronLeft, Sparkles } from 'lucide-react';
import { Button } from './ui/button';

// Spotlight walkthrough for building a first Daily Site Invoice (Sep 2026).
// Start with /daily-invoices?tour=1 (from the Invoicing guide) or the "Guided walkthrough" link.
// Each step targets a data-testid; steps whose target isn't on screen yet wait for it.
export const DAILY_INVOICE_TOUR_STEPS = [
  { id: 'di-job-select', title: '1 · Pick the job site', body: 'Choose the job (or project job) you are billing. The customer and their rate card come from this job.' },
  { id: 'di-date', title: '2 · Service date', body: 'The day the crew worked. One Daily Site Invoice covers one site on one day.' },
  { id: 'di-preview-btn', title: '3 · Build lines', body: 'Click Build lines. HinesTime lists everyone who worked that site that day with bill class, rate, hours and badge #. Nobody clocked in? You can add lines by hand next.', action: true },
  { id: 'di-col-bill_name', title: '4 · Rename a column header', body: 'Click any header (NAME, DESCRIPTION, QTY…) and type the wording your customer\'s upload template uses. "Make default for this customer" keeps it for next time.' },
  { id: 'di-add-line', title: '5 · Fix or add lines', body: 'Change bill class, hours (HH:MM), badge or employee # on any line. Add line adds a worker who was not clocked in; the trash icon removes a line.' },
  { id: 'di-meta-invoice_number', title: '6 · Invoice details', body: 'Leave Invoice # blank to auto-number, or type the number the customer requires. Fill P.O. number, terms, site, work order, tax and notes.' },
  { id: 'di-create-btn', title: '7 · Create invoice', body: 'Creates the invoice and lists it below. From the list you can download PDF / CSV / XLSX, Edit, Duplicate to another day, Archive, Delete, or tick several and Export selected XLSX.' },
];

const pad = 8;

export const DailyInvoiceTour = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [step, setStep] = useState(-1);
  const [rect, setRect] = useState(null);
  const active = step >= 0 && step < DAILY_INVOICE_TOUR_STEPS.length;
  const current = active ? DAILY_INVOICE_TOUR_STEPS[step] : null;

  useEffect(() => {
    if (new URLSearchParams(location.search).get('tour') === '1') setStep(0);
  }, [location.search]);

  const measure = useCallback(() => {
    if (!current) { setRect(null); return; }
    const el = document.querySelector(`[data-testid='${current.id}']`);
    if (!el) { setRect(null); return; }
    const r = el.getBoundingClientRect();
    if (r.bottom < 80 || r.top > window.innerHeight - 120) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
  }, [current]);

  useEffect(() => {
    if (!active) return undefined;
    measure();
    const t = setInterval(measure, 400);
    window.addEventListener('resize', measure);
    return () => { clearInterval(t); window.removeEventListener('resize', measure); };
  }, [active, measure]);

  const finish = () => {
    setStep(-1);
    const p = new URLSearchParams(location.search); p.delete('tour');
    navigate({ pathname: location.pathname, search: p.toString() ? `?${p}` : '' }, { replace: true });
  };

  if (!active) return null;
  const missing = !rect;
  const cardTop = rect ? Math.min(window.innerHeight - 220, rect.top + rect.height + pad + 12) : 120;
  const cardLeft = rect ? Math.max(16, Math.min(window.innerWidth - 400, rect.left)) : Math.max(16, window.innerWidth / 2 - 190);

  return (
    <div className="fixed inset-0 z-[90] pointer-events-none" data-testid="di-tour">
      {rect ? (
        <div className="absolute rounded-lg pointer-events-none transition-all duration-300"
          style={{ top: rect.top - pad, left: rect.left - pad, width: rect.width + pad * 2, height: rect.height + pad * 2, boxShadow: '0 0 0 9999px rgba(3,7,18,0.72), 0 0 0 3px #8BC34A' }} data-testid="di-tour-spotlight" />
      ) : <div className="absolute inset-0" style={{ background: 'rgba(3,7,18,0.72)' }} />}
      <div className="absolute w-[380px] max-w-[calc(100vw-32px)] rounded-xl p-4 shadow-2xl pointer-events-auto" style={{ top: cardTop, left: cardLeft, background: '#0F1923', border: '1px solid #8BC34A' }} data-testid="di-tour-card">
        <div className="flex items-start justify-between gap-2">
          <p className="text-sm font-bold text-white flex items-center gap-1.5"><Sparkles className="w-4 h-4 text-[#8BC34A]" /> {current.title}</p>
          <button onClick={finish} className="text-slate-400 hover:text-white" aria-label="Close walkthrough" data-testid="di-tour-close"><X className="w-4 h-4" /></button>
        </div>
        <p className="text-xs mt-2 leading-relaxed" style={{ color: '#CBD5E1' }} data-testid="di-tour-body">{current.body}</p>
        {missing && <p className="text-[11px] mt-2 text-amber-400" data-testid="di-tour-waiting">This control appears after the previous step — do that step, then click Next.</p>}
        <div className="flex items-center justify-between mt-3">
          <span className="text-[11px]" style={{ color: '#64748B' }} data-testid="di-tour-progress">Step {step + 1} of {DAILY_INVOICE_TOUR_STEPS.length}</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} data-testid="di-tour-back"><ChevronLeft className="w-3.5 h-3.5" /> Back</Button>
            {step < DAILY_INVOICE_TOUR_STEPS.length - 1
              ? <Button size="sm" onClick={() => setStep((s) => s + 1)} className="bg-[#8BC34A] hover:bg-[#7CB342] text-[#0B1321] font-semibold" data-testid="di-tour-next">Next <ChevronRight className="w-3.5 h-3.5" /></Button>
              : <Button size="sm" onClick={finish} className="bg-[#8BC34A] hover:bg-[#7CB342] text-[#0B1321] font-semibold" data-testid="di-tour-done">Done</Button>}
          </div>
        </div>
      </div>
    </div>
  );
};
